import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Swipeable } from 'react-swipeable';
import throttle from 'lodash.throttle';
import Icon from 'components/mediabay-ui/components/Icon';
import { PrevButton, NextButton } from './CarouselButtons';

const StyledCarousel = styled.div``;

const CarouselContainer = styled.div`
  position: relative;
  padding-left: 20px;

  @media ( min-width: 768px ) {
    padding-left: ${props => props.containerOffset}px;
  }
`;

const CarouselWrapper = styled.div`
  display: flex;
  box-sizing: border-box;
  width: 100%;
  transform: translate3d(-${props => props.stepWidth}px, 0, 0);
  transition: transform .2s ease-in-out;
`;

class Carousel extends React.Component {
  static defaultProps = {
    childOffset: 10,
    childWidth: 200,
    containerOffset: 0,
    detailsComponent: null,
    debug: false,
  }

  static propTypes = {
    childOffset: PropTypes.number,
    childWidth: PropTypes.number,
    containerOffset: PropTypes.number,
    detailsComponent: PropTypes.node,
    children: PropTypes.node.isRequired,
    debug: PropTypes.bool,
  }

  constructor(props) {
    super(props);
    this.state = {
      position: 0,
      stepWidth: 0,
      hasPrev: false,
      hasNext: false,
    };

    this.container = null;
    this.containerChildren = null;

    this.initTimeout = null;

    this.params = {
      containerWidth: 0,
      childWidth: 0,
      childOffsetWidth: 0,
      childrenFullWidth: 0,
      childrenInViewport: 0,
      childrenAmount: 0,
      stepsAmount: 0,
    };

    this.init = this.init.bind(this);
    this.resizeHandler = this.resizeHandler.bind(this);
    this.prev = this.prev.bind(this);
    this.next = this.next.bind(this);
  }

  componentDidMount() {
    this.init();
    window.addEventListener('resize', this.resizeHandler);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resizeHandler);
  }

  init() {
    const { childWidth, childOffset } = this.props;

    this.containerChildren = this.container.children;
    this.params.childrenAmount = this.containerChildren.length;
    this.params.containerWidth = this.container.clientWidth;
    this.params.childWidth = childWidth;
    this.params.childOffsetWidth = childWidth + childOffset;
    this.params.childrenFullWidth = this.params.childOffsetWidth * this.params.childrenAmount;
    this.params.childrenInViewport = (
      Math.floor(this.params.containerWidth / this.params.childOffsetWidth)
    );
    this.params.stepsAmount = (
      Math.round(this.params.childrenAmount / this.params.childrenInViewport)
    );

    if (this.params.childrenAmount) {
      for (let i = 0; i < this.params.childrenAmount; i += 1) {
        this.containerChildren[i].style.marginRight = `${this.props.childOffset}px`;
        this.containerChildren[i].style.width = `${this.props.childWidth}px`;
      }
    }
    if (this.params.childrenAmount >= this.params.childrenInViewport) {
      this.setState({
        hasNext: true,
      });
    }
  }

  resizeHandler() {
    this.init();
  }

  prev() {
    const { childOffsetWidth, childrenInViewport } = this.params;
    const { position, stepWidth, hasPrev } = this.state;

    if (hasPrev) {
      this.setState({
        stepWidth: stepWidth - (childOffsetWidth * childrenInViewport),
        position: position - 1,
        hasPrev: position >= 2,
        hasNext: true,
      });
    }
  }

  next() {
    const { stepWidth, position, hasNext } = this.state;
    const {
      childOffsetWidth,
      childrenInViewport,
      stepsAmount,
    } = this.params;

    if (hasNext) {
      this.setState({
        stepWidth: stepWidth + (childOffsetWidth * childrenInViewport),
        position: position + 1,
        hasPrev: true,
        hasNext: stepsAmount !== ((position + 1) + 1),
      });
    }
  }

  handleSwipe = throttle((isNext) => {
    if (isNext) {
      this.next();
    } else {
      this.prev();
    }
  }, 500, { trailing: false })

  render() {
    const { containerOffset, children } = this.props;

    return (
      <Swipeable
        onSwipedLeft={() => this.handleSwipe(true)}
        onSwipedRight={() => this.handleSwipe()}
      >
        <StyledCarousel {...this.props}>
          <CarouselContainer containerOffset={containerOffset}>
            <PrevButton
              icon="arrow-left"
              color="#fff"
              hasPrev={this.state.hasPrev}
              onClick={this.prev}
            >
              <Icon icon="arrow-left" style={{ position: 'absolute', top: '40%' }} />
            </PrevButton>
            <NextButton
              icon="arrow-right"
              color="#fff"
              hasNext={this.state.hasNext}
              onClick={this.next}
            >
              <Icon icon="arrow-right" style={{ position: 'absolute', top: '40%' }} />
            </NextButton>
            <div style={{ overflow: 'hidden' }}>
              <CarouselWrapper
                ref={(container) => { this.container = container; }}
                position={this.state.position}
                stepWidth={this.state.stepWidth}
              >
                {children}
              </CarouselWrapper>
            </div>
          </CarouselContainer>

          {this.props.detailsComponent}
        </StyledCarousel>
      </Swipeable>
    );
  }
}

export default Carousel;
